// kmlParser.js
//
// KML -> normalized lat/lon reference geometry for the Leaflet planning view.
//
// Why this exists: the TIFF workflow gets its georeferencing from a GeoTIFF's
// affine transform. A KML is already WGS84 lon/lat, so there is no pixel space
// and no transform to derive -- the coordinates ARE the answer. This module
// turns one or more KML files into a flat, render-ready feature list.
//
// KML features are REFERENCE geometry (plot boundary, AB heading line, cable
// baseline). They are drawn on the map for the operator to work against; they
// are NOT navigation lines. Nav lines are drawn by the operator on top and
// live in planningModel.js.
//
// Field exports arrive as a FOLDER of several single-Placemark KMLs (boundary,
// buffer, ab_line, cable_baseline), so parseKmlFiles() merges many files into
// one feature set.

import { kml as kmlToGeoJson } from '@tmcw/togeojson';

// export_object_type values seen in field exports, mapped to how the view
// should treat them. Unknown types still render, as 'other'.
const ROLE_BY_EXPORT_TYPE = {
  plot_grid_boundary: 'boundary',
  plot_grid_buffer: 'buffer',
  ab_line: 'guide',
  cable_baseline: 'guide',
};

export function roleFor(exportType) {
  return ROLE_BY_EXPORT_TYPE[exportType] || 'other';
}

// GeoJSON coordinates -> flat [[lon, lat], ...]. Drops any Z component, which
// KML often carries as a trailing 0 and which nothing downstream uses.
function flattenCoords(geometry) {
  if (!geometry) return [];
  const { type, coordinates } = geometry;
  const xy = (c) => [c[0], c[1]];
  switch (type) {
    case 'Point':
      return [xy(coordinates)];
    case 'LineString':
      return coordinates.map(xy);
    case 'Polygon':
      // Outer ring only. Inner rings (holes) are not part of the plot-grid
      // exports and have no meaning for row layout.
      return (coordinates[0] || []).map(xy);
    case 'MultiLineString':
      return coordinates.flat().map(xy);
    case 'MultiPolygon':
      return ((coordinates[0] || [])[0] || []).map(xy);
    default:
      return [];
  }
}

function kindFor(geometryType) {
  if (!geometryType) return 'other';
  if (geometryType.includes('Point')) return 'point';
  if (geometryType.includes('Polygon')) return 'polygon';
  if (geometryType.includes('LineString')) return 'line';
  return 'other';
}

// Parse one KML document (a DOM Document) into normalized features.
export function parseKmlDocument(doc, sourceName = '') {
  const gj = kmlToGeoJson(doc);
  const out = [];
  (gj.features || []).forEach((f, i) => {
    const coords = flattenCoords(f.geometry);
    if (coords.length === 0) return;
    const props = f.properties || {};
    const exportType = props.export_object_type || props.exportObjectType || '';
    out.push({
      id: `${sourceName || 'kml'}:${i}`,
      name: props.name || sourceName || `feature ${i}`,
      exportType,
      role: roleFor(exportType),
      kind: kindFor(f.geometry && f.geometry.type),
      closed: kindFor(f.geometry && f.geometry.type) === 'polygon',
      coords,
      source: sourceName,
    });
  });
  return out;
}

export function parseKmlText(text, sourceName = '') {
  const doc = new DOMParser().parseFromString(text, 'text/xml');
  const err = doc.querySelector('parsererror');
  if (err) throw new Error(`${sourceName || 'KML'}: not valid XML`);
  return parseKmlDocument(doc, sourceName);
}

// Bounding box over every feature. Returned in Leaflet's [[south, west],
// [north, east]] order so it can go straight into map.fitBounds().
export function boundsOf(features) {
  let minLon = Infinity, minLat = Infinity, maxLon = -Infinity, maxLat = -Infinity;
  features.forEach((f) => f.coords.forEach(([lon, lat]) => {
    if (lon < minLon) minLon = lon;
    if (lon > maxLon) maxLon = lon;
    if (lat < minLat) minLat = lat;
    if (lat > maxLat) maxLat = lat;
  }));
  if (!isFinite(minLon)) return null;
  return { minLon, minLat, maxLon, maxLat, leaflet: [[minLat, minLon], [maxLat, maxLon]] };
}

// Read + parse a list of File objects (the multi-select upload). Bad files are
// reported rather than thrown, so one malformed KML in a folder of four does
// not lose the other three.
export async function parseKmlFiles(files) {
  const features = [];
  const errors = [];
  for (const file of files) {
    try {
      const text = await file.text();
      features.push(...parseKmlText(text, file.name));
    } catch (e) {
      errors.push({ file: file.name, message: e.message });
    }
  }
  return { features, errors, bounds: boundsOf(features) };
}
